import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import { createAppointmentByDoctorApi, fetchLastScansApi } from '../services/apiService';
import {
  appointmentDetailsApi,
  cancelAppointmentApi,
  rescheduleAppointmentApi,
  absenceAppointmentApi,
  getAbsenceReasonApi,
  getCancelReasonApi,
  getRescheduleReasonApi,
  getAppointmentMessageApi,
  sendAppointmentMessageApi,
  dateAvailabilityApi,
  updatePriorityApi,
  choseChildApi,
} from '../services/appointmentService';

import {
  getDelayReasonApi,
  doctorFetchAppointmentsApi,
  getQuestionApi,
  getAppointmentBookingStep1
} from "@/services/apiService"

// ============================================================================
// Query Hooks
// ============================================================================


/**
 * fetch quertions 
 * TODO make sure to migrate this to another query specially for configs
 */

export function useGetQuesions(data, options = {}) {
  return useQuery({
    queryKey: queryKeys.configs.getQuersions(),
    queryFn: async ({ signal }) => {
      const response = await getQuestionApi(data)
      return response?.data?.data ?? response?.data ?? []
    },
    staleTime: 1_000 * 60 * 60 * 4,
    gcTime: 1_000 * 60 * 60 * 4,
    meta: {
      persist: true
    },
    ...options
  })
}

/**
 * fetch appointment booking step 1
 */
export function useGetAppointmentBookingStep1(data, options = {}) {
  return useQuery({
    queryKey: queryKeys.configs.getAppointmentBookingStep1(),
    queryFn: async ({ signal }) => {
      const response = await getAppointmentBookingStep1(data)
      return response?.data ?? []
    },
    staleTime: 1_000 * 60 * 60 * 4,
    gcTime: 1_000 * 60 * 60 * 4,
    meta: {
      persist: true
    },
    ...options
  })
}


/**
 * Fetch appointment from server
 */
export function useFetchAppointments(interval, options = {}) {
  return useQuery({
    queryKey: queryKeys.appointments.fetchAll(interval),
    queryFn: async ({ signal }) => {
      const response = await doctorFetchAppointmentsApi(interval)
      return response?.data ?? []
    },
    staleTime: 1_000 * 60 * 60 * 4,
    gcTime: 1_000 * 60 * 60 * 4,
    meta: {
      persist: true
    },
    ...options
  })
}


/**
 * Fetch detailed appointment information by appointment ID
 */
export function useAppointmentDetail(appointmentId, options = {}) {
  return useQuery({
    queryKey: queryKeys.appointments.detail(appointmentId),
    queryFn: async ({ signal }) => {
      const response = await appointmentDetailsApi({ appointmentId, signal });
      return response?.data?.data ?? response?.data ?? null;
    },
    enabled: Boolean(appointmentId),
    ...options,
  });
}

/**
 * Fetch messages for a specific appointment
 */
export function useAppointmentMessages(appointmentId, options = {}) {
  return useQuery({
    queryKey: queryKeys.appointments.messages(appointmentId),
    queryFn: async ({ signal }) => {
      const response = await getAppointmentMessageApi({ appointment_id: appointmentId, signal });
      return response?.data?.data ?? response?.data?.messages ?? response?.data ?? [];
    },
    enabled: Boolean(appointmentId),
    ...options,
  });
}

/**
 * Parse and normalize availability API response structure
 */
export function parseAvailabilityResponse(response, date) {
  const resData = response?.data;
  if (!resData || resData.res === false) {
    return {
      res: false,
      msg: resData?.msg || "Failed to fetch doctor availability",
      availableSlots: [],
      bookedSlots: [],
      holidays: [],
      futureDays: 0,
      raw: resData,
    };
  }

  const rawHolidays = Array.isArray(resData.holiday) ? resData.holiday : [];
  const holidays = rawHolidays.map((item) =>
    typeof item === "object" && item?.holiday_date ? item.holiday_date : item
  );
  const isHoliday = Boolean(date && holidays.includes(date));
  const availableSlots = isHoliday
    ? []
    : Array.isArray(resData.data)
      ? resData.data
      : [];
  const bookedSlots = Array.isArray(resData.notAvailable)
    ? resData.notAvailable.map((item) =>
      typeof item === "object" && item?.time ? item.time : item
    )
    : [];
  const futureDays = parseInt(resData.future_days || 0, 10);

  return {
    res: true,
    data: availableSlots,
    availableSlots,
    bookedSlots,
    holidays,
    isHoliday,
    futureDays,
    raw: resData,
  };
}

/**
 * Fetch doctor availability for a specific date with persistent cache.
 * Availability is cached for 24 hours (for the rest of the day) as it changes rarely.
 */
export function useAppointmentAvailability(
  doctorId,
  date,
  consultationType = "For online consultation",
  options = {}
) {
  // Support options as 3rd argument if consultationType is an object
  const resolvedType =
    typeof consultationType === "string"
      ? consultationType
      : "For online consultation";
  const resolvedOptions =
    typeof consultationType === "object" ? consultationType : options;

  return useQuery({
    queryKey: queryKeys.appointments.availability(doctorId, date, resolvedType),
    queryFn: async () => {
      let formData;
      if (typeof FormData !== "undefined") {
        formData = new FormData();
        formData.append("doctor_id", doctorId);
        formData.append("date", date);
        formData.append("consultation_types", resolvedType);
      } else {
        formData = {
          doctor_id: doctorId,
          date,
          consultation_types: resolvedType,
        };
      }
      const response = await dateAvailabilityApi(formData);
      return parseAvailabilityResponse(response, date);
    },
    enabled: Boolean(doctorId && date),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - fresh for the rest of the day
    gcTime: 24 * 60 * 60 * 1000, // Retained in persistent cache
    meta: { persist: true },
    ...resolvedOptions,
  });
}

export const useDoctorAvailability = useAppointmentAvailability;

/**
 * Imperatively fetch or retrieve cached doctor availability using QueryClient
 */
export async function fetchDoctorAvailability(
  queryClient,
  doctorId,
  date,
  consultationType = "For online consultation"
) {
  const queryKey = queryKeys.appointments.availability(
    doctorId,
    date,
    consultationType
  );
  return queryClient.fetchQuery({
    queryKey,
    queryFn: async () => {
      let formData;
      if (typeof FormData !== "undefined") {
        formData = new FormData();
        formData.append("doctor_id", doctorId);
        formData.append("date", date);
        formData.append("consultation_types", consultationType);
      } else {
        formData = {
          doctor_id: doctorId,
          date,
          consultation_types: consultationType,
        };
      }
      const response = await dateAvailabilityApi(formData);
      return parseAvailabilityResponse(response, date);
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    meta: { persist: true },
  });
}

/**
 * Fetch appointment cancellation/reschedule/absence reasons
 * Results are cached for 5 minutes (300,000 ms) in memory
 */
export function useAppointmentReasons(type = 'cancel', options = {}) {
  return useQuery({
    queryKey: queryKeys.appointments.reasons(type),
    queryFn: async ({ signal }) => {
      let response;
      if (type === 'absence') {
        response = await getAbsenceReasonApi({ signal });
      } else if (type === 'reschedule') {
        response = await getRescheduleReasonApi({ signal });
      } else if (type === "delay") {
        response = await getDelayReasonApi({ signal })
      } else {
        response = await getCancelReasonApi({ signal });
      }
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes freshness as requested
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Imperative fetch helper for appointment reasons
 */
export async function fetchAppointmentReasons(queryClient, type = 'cancel') {
  return queryClient.fetchQuery({
    queryKey: queryKeys.appointments.reasons(type),
    queryFn: async () => {
      let response;
      if (type === 'absence') {
        response = await getAbsenceReasonApi();
      } else if (type === 'reschedule') {
        response = await getRescheduleReasonApi();
      } else if (type === "delay") {
        response = await getDelayReasonApi();
      } else {
        response = await getCancelReasonApi();
      }
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Mutation Hooks (with Targeted Invalidation & Optimistic Updates)
// ============================================================================

/**
 * Cancel an appointment with targeted cache invalidation
 */
export function useCancelAppointment(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => cancelAppointmentApi(variables),
    onSuccess: (data, variables) => {
      const appointmentId = variables?.appointment_id || variables?.id;
      if (appointmentId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.detail(appointmentId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.lists(),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Reschedule an appointment with targeted cache invalidation
 */
export function useRescheduleAppointment(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => rescheduleAppointmentApi(variables),
    onSuccess: (data, variables) => {
      const appointmentId = variables?.appointment_id || variables?.id;
      if (appointmentId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.detail(appointmentId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.lists(),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Mark appointment as absent with targeted cache invalidation
 */
export function useAbsenceAppointment(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => absenceAppointmentApi(variables),
    onSuccess: (data, variables) => {
      const appointmentId = variables?.appointment_id || variables?.id;
      if (appointmentId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.detail(appointmentId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.lists(),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Optimistic mutation for updating appointment priority
 */
export function useUpdatePriority(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => updatePriorityApi(variables),
    onMutate: async (newPriorityPayload) => {
      const appointmentId = newPriorityPayload?.appointment_id || newPriorityPayload?.id;
      if (!appointmentId) return {};

      const queryKey = queryKeys.appointments.detail(appointmentId);

      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousAppointment = queryClient.getQueryData(queryKey);

      // Optimistically update the cache
      if (previousAppointment) {
        queryClient.setQueryData(queryKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            priority: newPriorityPayload.priority ?? old.priority,
          };
        });
      }

      return { previousAppointment, queryKey };
    },
    onError: (err, newPriorityPayload, context) => {
      // Rollback to previous state on error
      if (context?.previousAppointment && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousAppointment);
      }
      options.onError?.(err, newPriorityPayload, context);
    },
    onSettled: (data, error, variables, context) => {
      // Targeted revalidation to sync with server
      if (context?.queryKey) {
        queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.lists() });
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Optimistic mutation for sending appointment message
 */
export function useSendAppointmentMessage(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => sendAppointmentMessageApi(variables),
    onMutate: async (newMessagePayload) => {
      const appointmentId = newMessagePayload?.appointment_id;
      if (!appointmentId) return {};

      const queryKey = queryKeys.appointments.messages(appointmentId);

      await queryClient.cancelQueries({ queryKey });

      const previousMessages = queryClient.getQueryData(queryKey) || [];

      // Create optimistic message object
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        message: newMessagePayload.message || newMessagePayload.content,
        created_at: new Date().toISOString(),
        is_doctor: true,
        is_pending: true,
        ...newMessagePayload,
      };

      queryClient.setQueryData(queryKey, (old = []) => {
        return Array.isArray(old) ? [...old, optimisticMessage] : [optimisticMessage];
      });

      return { previousMessages, queryKey };
    },
    onError: (err, variables, context) => {
      if (context?.previousMessages && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousMessages);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.queryKey) {
        queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Choose child/patient for appointment
 */
export function useChoseChild(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => choseChildApi(variables),
    onSuccess: (data, variables) => {
      const appointmentId = variables?.appointment_id;
      if (appointmentId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.detail(appointmentId),
        });
      }
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Create appointment by doctor
 */
export function useCreateAppointmentByDoctor(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => createAppointmentByDoctorApi(variables),
    onSuccess: (data, variables) => {
      // Invalidate relevant queries such as appointments list or schedule
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all,
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Fetch last scans for a doctor
 */
export function useLastScans(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.scans.lastScans(doctorId),
    queryFn: async () => {
      const response = await fetchLastScansApi(doctorId);
      return response?.data?.last_scans ?? [];
    },
    enabled: Boolean(doctorId),
    ...options,
  });
}
